import { Inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { SentryService } from '@rd-lib/sentry/sentry.service';
import {
  BRAND_NAME,
  PORTAL_NAME,
  USE_CUSTOM_PAGE_TITLE,
} from '@rd/core/tokens/rd-lib.token';

export interface PageTitleMap {
  'benefit-details': string;
  'benefit-success': string;
  'login-page': string;
  'home-page': string;
  'offer-details': string;
  'offer-listing': string;
  'gift-card-listing': string;
  'gift-card-checkout': string;
  'gift-card-review': string;
  'gift-card-success': string;
  'gift-card-manage': string;
  'redeem-cash-checkout': string;
  'redeem-cash-review': string;
  'redeem-cash-success': string;
  'select-carrier': string;
  'select-carrier-details': string;
  'transfer-connect-listing': string;
  'transfer-connect-checkout': string;
  'transfer-connect-review': string;
  'transfer-connect-success': string;
  'points-transaction': string;
  order: string;
  faqs: string;
  'privacy-policy': string;
  'contact-us': string;
  'terms-and-conditions': string;
  'select-card': string;
  'latest-updates': string;
  'not-found': string;
  'merchandise-landing-page': string;
  'merchandise-details': string;
  'merchandise-listing-all-products': string;
  'merchandise-listing-category-products': string;
  'shopping-cart-home': string;
  'shopping-cart-delivery': string;
  'shopping-cart-payment': string;
  'shopping-cart-success': string;
  'auto-redemption-settings-details': string;
  'auto-redemption-settings-review': string;
  'auto-redemption-settings-success': string;
  auto_redemption_settings: string;
  'points-share-details': string;
  'points-share-review': string;
  'points-share-success': string;
  'everyday-spend-checkout': string;
  'everyday-spend-review': string;
  'everyday-spend-success': string;
}

@Injectable({
  providedIn: 'root',
})
export class PageTitleService {
  pageTitleMap: PageTitleMap = {
    'benefit-details': 'benefit.details.page_title',
    'benefit-success': 'benefit.success.page_title',
    'login-page': 'login_page.page_title',
    'home-page': 'home_page.page_title',
    'offer-details': 'offer.details.page_title',
    'offer-listing': 'offer.listing.page_title',
    'gift-card-listing': 'gift_card.listing.page_title',
    'gift-card-checkout': 'gift_card.checkout.page_title',
    'gift-card-review': 'gift_card.review.page_title',
    'gift-card-success': 'gift_card.success.page_title',
    'gift-card-manage': 'gift_card.manage.page_title',
    'redeem-cash-checkout': 'redeem_cash_v2.checkout.page_title',
    'redeem-cash-review': 'redeem_cash_v2.review.page_title',
    'redeem-cash-success': 'redeem_cash_v2.success.page_title',
    'select-carrier': 'select_carrier.page_title',
    'select-carrier-details': 'select_carrier.details.page_title',
    'transfer-connect-listing': 'transfer_v2.listing.page_title',
    'transfer-connect-checkout': 'transfer_v2.checkout.page_title',
    'transfer-connect-review': 'transfer_v2.review.page_title',
    'transfer-connect-success': 'transfer_v2.success.page_title',
    'points-transaction': 'points_transaction.page_title',
    order: 'order.page_title',
    faqs: 'faqs.page_title',
    'privacy-policy': 'privacy_policy.page_title',
    'contact-us': 'contact-us.page_title',
    'terms-and-conditions': 'terms_and_conditions.page_title',
    'select-card': 'select_card.page_title',
    'latest-updates': 'latest_updates.page_title',
    'not-found': 'not_found.page_title',
    'merchandise-landing-page': 'merchandise.landing.page_title',
    'merchandise-details': 'merchandise.details.page_title',
    'merchandise-listing-all-products':
      'merchandise.listing.page_title.all_products',
    'merchandise-listing-category-products':
      'merchandise.listing.page_title.category',
    'shopping-cart-home': 'shopping_cart.home.page_title',
    'shopping-cart-delivery': 'shopping_cart.delivery.page_title',
    'shopping-cart-payment': 'shopping_cart.payment.page_title',
    'shopping-cart-success': 'shopping_cart.success.page_title',
    'auto-redemption-settings-details': 'auto_redemption.details.page_title',
    'auto-redemption-settings-review': 'auto_redemption.review.page_title',
    'auto-redemption-settings-success': 'auto_redemption.success.page_title',
    auto_redemption_settings: 'auto_redemption.settings.page_title',
    'points-share-details': 'points_share.details.page_title',
    'points-share-review': 'points_share.review.page_title',
    'points-share-success': 'points_share.success.page_title',
    'everyday-spend-checkout': 'everyday_spend.checkout.page_title',
    'everyday-spend-review': 'everyday_spend.review.page_title',
    'everyday-spend-success': 'everyday_spend.success.page_title',
  };
  pageTitleKey: keyof PageTitleMap;

  constructor(
    protected titleService: Title,
    protected translateService: TranslateService,
    protected sentryService: SentryService,
    @Inject(BRAND_NAME) protected brandName: string,
    @Inject(PORTAL_NAME) protected portalName: string,
    @Inject(USE_CUSTOM_PAGE_TITLE) protected useCustomPageTitle: boolean,
  ) {
    this.translateService.onLangChange.subscribe(() => {
      if (this.pageTitleKey) {
        this.setPageTitle(this.pageTitleKey);
      }
    });
  }

  setPageTitle(
    page: keyof PageTitleMap,
    params?: { [key: string]: string },
  ): void {
    if (this.useCustomPageTitle) {
      this.pageTitleKey = page;
      const pageTitle = this.translateService.instant(this.pageTitleMap[page], {
        ...this.defaultTranslationParams(),
        ...params,
      });

      if (!pageTitle) {
        const error = `Missing translation in page ${page} for key ${this.pageTitleMap[page]}`;

        this.sentryService.manualError(error);
      }

      this.titleService.setTitle(pageTitle);
    }
  }

  protected defaultTranslationParams(): { [key: string]: string } {
    return {
      brandName: this.brandName,
      portalName: this.portalName,
    };
  }
}
